import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Send, Bot, User, Sparkles, CheckCircle, 
  AlertCircle, ChevronRight, BarChart3, FileText, Edit3,
  RefreshCw, Download
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 15 categories with their sub-items
const SCORECARD_STRUCTURE = {
  section1: {
    name: 'Central Control',
    categories: {
      leadership: {
        name: 'Leadership & General Management',
        items: [
          'Top Management Expectations', 'Safety and injury goals', 'Senior management feedback/recognition',
          'Risk manager influence/accountability', 'Cost allocation system', 'Loss reserves, financial reporting practices',
          'Control of Total Cost of Risk', 'Board of Education Policy', 'Risk Management Committee',
          'Asset Valuation Services', 'Custom programs', 'Emerging exposures research'
        ]
      },
      riskFinancing: {
        name: 'Risk Financing',
        items: [
          'Service provider selection and quality control', 'Workers\' compensation', 'Unemployment compensation',
          'Property/Crime/Fidelity/APD/Energy Systems', 'Motor Vehicle Liability', 'General Liability',
          'School Leaders Liability', 'Environmental Impairment Liability', 'Fiduciary Liability',
          'Cyber Liability', 'Umbrella Liability', 'Contractual Risk Transfer'
        ]
      },
      hrPractices: {
        name: 'HR Practices',
        items: [
          'Workers\' Compensation orientation', 'Employee screening and placement', 'Employee Rights Posting',
          'Employee notice of WCB changes', 'Design of employee benefits program', 'Post Hire Medical Questionnaire',
          'Post Accident Drug Testing', 'Driver Qualification'
        ]
      },
      incidentPrevention: {
        name: 'Incident Prevention Practices',
        items: [
          'Exposure ID, Control, Audit/Oversight', 'Active Caring/Behavior Based Safety', 'Accident investigation',
          'Safety Meetings', 'Employee Safety Training', 'Manager Safety Training',
          'Risk Mgt/Safety Staff Training', 'Critical Alarms'
        ]
      }
    }
  },
  section2: {
    name: 'Gray Matter',
    categories: {
      enterpriseRisk: {
        name: 'Enterprise Risk Assessment',
        items: [
          'Strategic Risk Committee', 'Risk Mapping Exercises', 'Business Continuity Assessment',
          'Enterprise Risk Framework', 'Risk Intelligent Enterprise', 'ISO 31000',
          'GFO Risk Management Standards', 'Organizational Dynamics'
        ]
      },
      riskCommunication: {
        name: 'Risk Communication',
        items: [
          'Interpersonal dialogue and presentations', 'District Employee Newsletter', 'Community Awareness and Publicity',
          'Internal Staff Reports', 'Annual Report', 'District Website Links with External Resources', 'Risk Cartography'
        ]
      },
      metrics: {
        name: 'Metrics and Measures',
        items: [
          'Performance KPIs and Action Plans', 'Loss Data Analysis', 'Safety & Health Process Audits',
          'Claims Management Audits', 'Risk Intelligence', 'RMIS System'
        ]
      }
    }
  },
  section3: {
    name: 'Risk Improvement',
    categories: {
      districtPrograms: {
        name: 'District Wide Programs',
        items: [
          'Fire/Arson Prevention', 'Electrical Safety', 'Playground Safety', 'Extra-Curricular Events and Field Trips',
          'Energy Systems/Mechanical Breakdown', 'AED Deployment and Use', 'Valuable Papers/Money/Securities Preservation',
          'Identity Theft Prevention', 'Community Use of Facilities', 'Vocational Ed Safety', 'Cyber Security'
        ]
      },
      employeeSafety: {
        name: 'Employee Safety Programs',
        items: [
          'Slip, Trip & Fall Prevention', 'Lifting/Material Handling', 'Cut Prevention', 'Burn Prevention',
          'Lock-out - Tag-out', 'Emergency Preparedness', 'Ergonomics', 'Confined Space Entry',
          'Hazardous Materials', 'Powered Industrial Trucks', 'Blood borne Pathogens',
          'Hearing Conservation', 'Personal Protective Equipment'
        ]
      },
      equity: {
        name: 'Equity & Violence Prevention',
        items: [
          'FAPE/LRE Practices', 'First Amendment Practices', 'Fair Employment Practices', 'Fair Attendance Zoning',
          'Fair Discipline', 'Gender Equity in Athletics', 'Sexual Misconduct Prevention', 'School Climate Improvement',
          'Hazing Prevention', 'Acquired Brain Injury Prevention', 'Digital Citizenship'
        ]
      },
      totalHealth: {
        name: 'Total Health',
        items: [
          'Indoor Air Quality', 'Outdoor Environmental Quality', 'Employee Wellness', 'Substance Abuse Prevention',
          'Teen Pregnancy Prevention', 'Smoking Prevention', 'Eating Disorder Prevention'
        ]
      }
    }
  },
  section4: {
    name: 'Ancillary Operations',
    categories: {
      discreteServices: {
        name: 'Discrete Services',
        items: [
          'Pupil Transportation', 'Nutrition', 'Athletics', 'Maintenance', 'Custodial',
          'Exceptional and Special Ed', 'Nursing', 'Science & Labs', 'Campus Security and Emergency Ops Planning'
        ]
      },
      supplyChain: {
        name: 'Supply Chain & Construction',
        items: [
          'Purchasing', 'Warehouse Inventory System', 'Capital Planning', 'Service Professional Selection',
          'Construction Management', 'Project Risk Management', 'Project Insurance', 'Contractual Risk Transfer'
        ]
      },
      injuryManagement: {
        name: 'Injury Management',
        items: [
          'Communication with injured worker', 'PPO Program', 'Medical Provider Relations', 'Nurse Triage',
          'Case Management', 'Transitional Duty Program (ERTW)', 'Transitional Duty Coordinator', 'Transitional Duty Job Bank'
        ]
      },
      claimsManagement: {
        name: 'Claims Management',
        items: [
          'Service Professional Selection', 'Injury Reporting Timeliness', 'Employee Claims Reporting',
          'Supervisor Claims Reporting', 'Internal Claims Handling', 'External Vendor Claims Management',
          'Claims Review', 'Litigation Management'
        ]
      }
    }
  }
};

// Assessment questions that map to multiple scorecard items
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: "Let's start with leadership. How does your district's executive team approach risk management? Tell me about board policies, committees, and how risk is discussed at the leadership level.",
    mappings: ['leadership'],
    section: 'section1'
  },
  {
    id: 'q2',
    question: "How does your district handle insurance and risk financing? Walk me through your coverage areas - workers' comp, liability, cyber, property - and how you select providers.",
    mappings: ['riskFinancing'],
    section: 'section1'
  },
  {
    id: 'q3',
    question: "Tell me about your HR practices related to risk - employee screening, orientation on safety/workers' comp, drug testing policies, and driver qualifications.",
    mappings: ['hrPractices'],
    section: 'section1'
  },
  {
    id: 'q4',
    question: "Describe your incident prevention approach. How do you conduct safety training, accident investigations, and what safety meeting cadence do you follow?",
    mappings: ['incidentPrevention'],
    section: 'section1'
  },
  {
    id: 'q5',
    question: "Does your district have an enterprise risk assessment process? Tell me about risk committees, risk mapping, business continuity planning, and any frameworks you follow (ISO 31000, etc.).",
    mappings: ['enterpriseRisk'],
    section: 'section2'
  },
  {
    id: 'q6',
    question: "How do you communicate about risk across your district? Think about newsletters, reports, community outreach, website resources, and internal presentations.",
    mappings: ['riskCommunication'],
    section: 'section2'
  },
  {
    id: 'q7',
    question: "What metrics and measures do you track for risk management? Do you have KPIs, conduct audits, analyze loss data, or use any risk management information systems?",
    mappings: ['metrics'],
    section: 'section2'
  },
  {
    id: 'q8',
    question: "Tell me about district-wide safety programs - fire prevention, playground safety, cyber security, AED deployment, field trip safety, and facility use policies.",
    mappings: ['districtPrograms'],
    section: 'section3'
  },
  {
    id: 'q9',
    question: "What employee safety programs do you have in place? Consider slip/fall prevention, ergonomics, PPE, hazmat handling, emergency preparedness, and specific hazard training.",
    mappings: ['employeeSafety'],
    section: 'section3'
  },
  {
    id: 'q10',
    question: "How does your district address equity and violence prevention? Tell me about Title IX, fair discipline, anti-hazing, digital citizenship, school climate initiatives, and misconduct prevention.",
    mappings: ['equity'],
    section: 'section3'
  },
  {
    id: 'q11',
    question: "Describe your total health initiatives - indoor/outdoor air quality, employee wellness programs, and any student health prevention programs you run.",
    mappings: ['totalHealth'],
    section: 'section3'
  },
  {
    id: 'q12',
    question: "Let's talk about discrete services. How do you manage risk in transportation, athletics, nutrition services, maintenance, nursing, and campus security?",
    mappings: ['discreteServices'],
    section: 'section4'
  },
  {
    id: 'q13',
    question: "Tell me about your supply chain and construction risk management - purchasing practices, capital planning, contractor selection, and project insurance.",
    mappings: ['supplyChain'],
    section: 'section4'
  },
  {
    id: 'q14',
    question: "How do you manage workplace injuries when they occur? Walk me through your process from injury to return-to-work, including medical providers, case management, and transitional duty.",
    mappings: ['injuryManagement'],
    section: 'section4'
  },
  {
    id: 'q15',
    question: "Finally, describe your claims management process - reporting timelines, internal vs external handling, claims review, and litigation management.",
    mappings: ['claimsManagement'],
    section: 'section4'
  }
];

export default function AIAssessmentPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('intro'); // intro, chat, review, results
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [scores, setScores] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startAssessment = () => {
    setCurrentStep('chat');
    setSessionId(`assessment_${Date.now()}`);
    // Add first question
    const firstQuestion = ASSESSMENT_QUESTIONS[0];
    setMessages([{
      role: 'assistant',
      content: `Great! Let's begin your Risk Intelligence Assessment. I'll ask you ${ASSESSMENT_QUESTIONS.length} questions about different aspects of your district's risk management practices. Based on your responses, I'll generate a comprehensive scorecard.\n\n**Question 1 of ${ASSESSMENT_QUESTIONS.length}:**\n\n${firstQuestion.question}`,
      questionIndex: 0
    }]);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      // Send to AI for analysis and scoring
      const response = await axios.post(`${API_URL}/api/assessment/ai-analyze`, {
        question: ASSESSMENT_QUESTIONS[currentQuestionIndex],
        answer: input.trim(),
        session_id: sessionId
      }, { withCredentials: true });

      const { inferred_scores, follow_up, analysis } = response.data;

      // Store the inferred scores
      const questionMapping = ASSESSMENT_QUESTIONS[currentQuestionIndex].mappings[0];
      setScores(prev => ({
        ...prev,
        [questionMapping]: inferred_scores
      }));

      // Check if we have more questions
      const nextIndex = currentQuestionIndex + 1;
      
      if (nextIndex < ASSESSMENT_QUESTIONS.length) {
        const nextQuestion = ASSESSMENT_QUESTIONS[nextIndex];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${analysis}\n\n---\n\n**Question ${nextIndex + 1} of ${ASSESSMENT_QUESTIONS.length}:**\n\n${nextQuestion.question}`,
          questionIndex: nextIndex,
          scores: inferred_scores
        }]);
        setCurrentQuestionIndex(nextIndex);
      } else {
        // Assessment complete
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `${analysis}\n\n---\n\n**Assessment Complete!** 🎉\n\nI've analyzed all your responses and generated scores for 136 items across 15 categories. Click "Review Scorecard" to see your results and make any adjustments.`,
          scores: inferred_scores,
          complete: true
        }]);
        setCurrentStep('review');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I encountered an issue analyzing that response. Let me continue with the next question.",
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const calculateTotalScore = () => {
    let totalScore = 0;
    let totalItems = 0;
    
    Object.values(scores).forEach(categoryScores => {
      Object.values(categoryScores).forEach(score => {
        totalScore += score;
        totalItems++;
      });
    });
    
    return totalItems > 0 ? Math.round((totalScore / (totalItems * 5)) * 100) : 0;
  };

  const getCategoryScore = (categoryKey) => {
    const categoryScores = scores[categoryKey];
    if (!categoryScores) return 0;
    
    const values = Object.values(categoryScores);
    if (values.length === 0) return 0;
    
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    return Math.round((avg / 5) * 100);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600 bg-emerald-50';
    if (score >= 60) return 'text-blue-600 bg-blue-50';
    if (score >= 40) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const saveAndViewScorecard = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/scorecard/save`, {
        scores: scores,
        session_id: sessionId
      }, { withCredentials: true });
      
      // Navigate to the scorecard page
      navigate('/scorecard');
    } catch (error) {
      console.error('Failed to save scorecard:', error);
      alert('Failed to save scorecard. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Intro Screen
  if (currentStep === 'intro') {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-outfit text-xl font-bold text-slate-900">AI Risk Assessment</h1>
                <p className="text-sm text-slate-500">Conversational Scorecard Builder</p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto p-6">
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center" data-testid="assessment-intro">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="font-outfit text-3xl font-bold text-slate-900 mb-4">
              Smart Risk Assessment
            </h2>
            
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Instead of filling out 140+ survey questions, have a conversation with our AI. 
              I'll ask you 15 strategic questions and intelligently score all 136 items on your 
              Balanced Scorecard based on your responses.
            </p>

            <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-outfit font-bold text-blue-600">15</p>
                <p className="text-sm text-slate-600">Questions</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-outfit font-bold text-emerald-600">136</p>
                <p className="text-sm text-slate-600">Items Scored</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-outfit font-bold text-purple-600">~20</p>
                <p className="text-sm text-slate-600">Minutes</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto text-left">
              <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
              <ol className="text-sm text-blue-800 space-y-1">
                <li>1. Answer questions about your district's risk practices</li>
                <li>2. AI analyzes responses and infers scores for related items</li>
                <li>3. Review and adjust any AI-generated scores</li>
                <li>4. Get your complete Balanced Scorecard with recommendations</li>
              </ol>
            </div>

            <button
              onClick={startAssessment}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all inline-flex items-center gap-2 text-lg"
              data-testid="start-ai-assessment"
            >
              Start AI Assessment
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Review/Results Screen
  if (currentStep === 'review' || currentStep === 'results') {
    const totalScore = calculateTotalScore();
    
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
                <ArrowLeft className="w-5 h-5 text-slate-500" />
              </Link>
              <div>
                <h1 className="font-outfit text-xl font-bold text-slate-900">Balanced Scorecard Results</h1>
                <p className="text-sm text-slate-500">AI-Generated Assessment</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 hover:bg-slate-100 rounded-lg transition-all" title="Export PDF">
                <Download className="w-5 h-5 text-slate-500" />
              </button>
              <button 
                onClick={() => { setCurrentStep('intro'); setScores({}); setMessages([]); setCurrentQuestionIndex(0); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-all" 
                title="Start Over"
              >
                <RefreshCw className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto p-6">
          {/* Overall Score */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 mb-6" data-testid="overall-score-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Overall Risk Intelligence Score</p>
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-outfit font-bold text-slate-900">{totalScore}%</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(totalScore)}`}>
                    {totalScore >= 80 ? 'Excellent' : totalScore >= 60 ? 'Good' : totalScore >= 40 ? 'Fair' : 'Needs Work'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Categories Assessed</p>
                <p className="text-2xl font-outfit font-bold text-slate-900">{Object.keys(scores).length}/15</p>
              </div>
            </div>
          </div>

          {/* Section Cards */}
          <div className="space-y-6">
            {Object.entries(SCORECARD_STRUCTURE).map(([sectionKey, section]) => (
              <div key={sectionKey} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-200">
                  <h3 className="font-outfit text-lg font-semibold text-slate-900">{section.name}</h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(section.categories).map(([catKey, category]) => {
                      const catScore = getCategoryScore(catKey);
                      const hasScores = scores[catKey] && Object.keys(scores[catKey]).length > 0;
                      
                      return (
                        <div 
                          key={catKey} 
                          className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-all"
                          data-testid={`category-${catKey}`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-medium text-slate-900">{category.name}</h4>
                              <p className="text-xs text-slate-500">{category.items.length} items</p>
                            </div>
                            {hasScores ? (
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(catScore)}`}>
                                {catScore}%
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-500">
                                Pending
                              </span>
                            )}
                          </div>
                          
                          {hasScores && (
                            <div className="space-y-2">
                              {category.items.slice(0, 3).map((item, idx) => {
                                const itemScore = scores[catKey]?.[item] || 0;
                                return (
                                  <div key={idx} className="flex items-center justify-between text-sm">
                                    <span className="text-slate-600 truncate flex-1 mr-2">{item}</span>
                                    <div className="flex items-center gap-1">
                                      {[1,2,3,4,5].map(n => (
                                        <div 
                                          key={n} 
                                          className={`w-2 h-2 rounded-full ${n <= itemScore ? 'bg-blue-500' : 'bg-slate-200'}`}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                              {category.items.length > 3 && (
                                <button className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                  <Edit3 className="w-3 h-3" />
                                  View & edit all {category.items.length} items
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Items */}
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-6" data-testid="action-items">
            <h3 className="font-outfit text-lg font-semibold text-amber-900 mb-4">Recommended Action Items</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Strengthen Enterprise Risk Assessment</p>
                  <p className="text-sm text-amber-700">Consider implementing a formal risk framework like ISO 31000</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Improve Risk Communication</p>
                  <p className="text-sm text-amber-700">Develop regular risk reporting cadence to board and staff</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">Enhance Metrics & Measures</p>
                  <p className="text-sm text-amber-700">Implement KPIs for safety performance tracking</p>
                </div>
              </div>
            </div>
          </div>

          {/* Save & Continue Button */}
          <div className="mt-6 flex justify-center gap-4">
            <button
              onClick={saveAndViewScorecard}
              disabled={saving}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition-all flex items-center gap-2 text-lg"
              data-testid="save-scorecard-btn"
            >
              {saving ? 'Saving...' : 'Save & View Full Scorecard'}
              <BarChart3 className="w-5 h-5" />
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Chat Screen
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-outfit text-xl font-bold text-slate-900">AI Risk Assessment</h1>
                <p className="text-sm text-slate-500">Question {currentQuestionIndex + 1} of {ASSESSMENT_QUESTIONS.length}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-32 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                style={{ width: `${((currentQuestionIndex + 1) / ASSESSMENT_QUESTIONS.length) * 100}%` }}
              />
            </div>
            <span className="text-sm text-slate-500">{Math.round(((currentQuestionIndex + 1) / ASSESSMENT_QUESTIONS.length) * 100)}%</span>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto flex flex-col">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : ''}`}
              data-testid={`message-${index}`}
            >
              {message.role === 'assistant' && (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-xl p-4 ${
                  message.role === 'user'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white border border-slate-200 text-slate-900'
                }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                {message.scores && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">Inferred scores for this category:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(message.scores).slice(0, 4).map(([item, score]) => (
                        <span key={item} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs">
                          <span className="truncate max-w-[100px]">{item}</span>
                          <span className="font-bold text-blue-600">{score}/5</span>
                        </span>
                      ))}
                      {Object.keys(message.scores).length > 4 && (
                        <span className="text-xs text-slate-500">+{Object.keys(message.scores).length - 4} more</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {message.role === 'user' && (
                <div className="w-10 h-10 rounded-lg bg-slate-900 flex-shrink-0 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          ))}
          
          {loading && (
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {currentStep === 'chat' && (
          <div className="border-t border-slate-200 bg-white p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex gap-4">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Describe your district's practices..."
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  rows={3}
                  disabled={loading}
                  data-testid="assessment-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 self-end"
                  data-testid="send-response"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-2 text-center">
                Be as detailed as possible - the AI will infer scores for multiple items based on your response
              </p>
            </div>
          </div>
        )}

        {currentStep === 'review' && (
          <div className="border-t border-slate-200 bg-white p-4">
            <div className="max-w-4xl mx-auto flex justify-center">
              <button
                onClick={() => setCurrentStep('results')}
                className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-medium hover:from-emerald-700 hover:to-teal-700 transition-all flex items-center gap-2"
                data-testid="view-scorecard"
              >
                <BarChart3 className="w-5 h-5" />
                View Full Scorecard
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
